export const PULSE_STATUSES = [
  { value: '', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'disabled', label: 'Disabled' },
  { value: 'expired', label: 'Expired' },
  { value: 'failed', label: 'Failed' },
];

export const PULSE_CATEGORIES = [
  { value: '', label: 'All categories' },
  { value: 'instinct', label: 'Instinct' },
  { value: 'knowledge', label: 'Knowledge' },
  { value: 'news', label: 'News' },
  { value: 'fan_story', label: 'Fan Story' },
  { value: 'hot_take', label: 'Hot Take' },
  { value: 'fan_wars', label: 'Fan Wars' },
];

export const PULSE_BUCKETS = [
  { value: 'instinct', label: 'Instinct' },
  { value: 'knowledge', label: 'Knowledge' },
  { value: 'news', label: 'News' },
  { value: 'fan_story', label: 'Fan Story' },
  { value: 'hot_take', label: 'Hot Take' },
  { value: 'fan_wars', label: 'Fan Wars' },
];

/** News / fan story / hot take copy targets (chars). */
export const COPY_LIMITS = {
  title: { min: 60, max: 70, soft: '60–70' },
  summary: { min: 140, max: 150, soft: '140–150' },
  question: { min: 60, max: 70, soft: '60–70' },
  optionLabel: { min: 2, max: 9, soft: '2–9' },
};

export function copyLengthHint(field, value) {
  const lim = COPY_LIMITS[field];
  if (!lim) return '';
  const n = String(value || '').length;
  return `${n}/${lim.max} (target ${lim.soft})`;
}

/** Mobile wire hero_image.presentation — omit/unknown → app bleed.
 *  bleed = photo band on top; backdrop = taller photo behind question (joystick forces backdrop). */
export const HERO_PRESENTATIONS = [
  { value: '', label: 'Default (bleed)' },
  { value: 'bleed', label: 'Bleed — photo band, content below' },
  { value: 'backdrop', label: 'Backdrop — photo behind question' },
];

export const HERO_ASPECT_RATIOS = [
  { value: '', label: 'Default' },
  { value: '1:1', label: '1:1' },
  { value: '4:3', label: '4:3' },
  { value: '3:4', label: '3:4' },
  { value: '16:9', label: '16:9' },
  { value: '9:16', label: '9:16' },
];

export function normalizeHeroPresentation(value) {
  if (value == null || value === '') return '';
  const v = String(value).toLowerCase();
  if (v === 'bleed' || v === 'backdrop') return v;
  return '';
}

export function normalizeHeroAspectRatio(value) {
  if (value == null || value === '') return '';
  const v = String(value).trim();
  return /^\d+:\d+$/.test(v) ? v : v;
}

export const MATCH_STATUS_LABELS = {
  1: 'upcoming',
  3: 'live',
  2: 'completed',
  4: 'cancelled',
};

export function statusChipColor(status) {
  if (status === 'active') return 'success';
  if (status === 'disabled') return 'warning';
  if (status === 'failed') return 'error';
  if (status === 'expired') return 'default';
  if (status === 'draft') return 'info';
  return 'default';
}

export function matchStatusChipColor(status) {
  if (status === 3 || status === 'live') return 'error';
  if (status === 1 || status === 'upcoming') return 'info';
  if (status === 2 || status === 'completed') return 'success';
  return 'default';
}

export function formatNumber(value) {
  if (value == null || Number.isNaN(Number(value))) return '0';
  return Number(value).toLocaleString();
}

/** 1 = first card the app pushes. null = not currently on the live deck. */
export function formatDeckPosition(value) {
  if (value == null || value === '') return '—';
  const n = Number(value);
  return Number.isFinite(n) ? String(n) : '—';
}

/** ISO timestamp → local string, or em dash when null/empty (expires_at open-ended). */
export function formatPulseDate(iso) {
  if (iso == null || iso === '') return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function isoToDatetimeLocal(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function datetimeLocalToIso(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

export function isFutureIso(iso) {
  if (!iso) return false;
  const d = new Date(iso);
  return !Number.isNaN(d.getTime()) && d.getTime() > Date.now();
}

export function emptyOption() {
  return { id: '', label: '' };
}

function sameOptions(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  return a.every((opt, i) => opt.id === b[i].id && opt.label === b[i].label);
}

function sameOptionIds(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  return a.every((opt, i) => String(opt.id) === String(b[i].id));
}

export function normalizeOptionRows(options) {
  return (options || [])
    .filter((o) => String(o.id || '').trim() && String(o.label || '').trim())
    .map((o) => ({ id: String(o.id).trim(), label: String(o.label).trim() }));
}

/** Prefer `options`; if empty, fill from `payload.wire` (Hot Takes / ratings). */
export function normalizeCardOptions(card) {
  if (Array.isArray(card?.options) && card.options.length) {
    return card.options.map((o) => ({
      id: String(o.id || o.value || ''),
      label: String(o.label || o.name || o.id || ''),
      is_correct: !!o.is_correct,
    }));
  }
  const wire = card?.payload?.wire || card?.wire || [];
  if (!Array.isArray(wire)) return [];
  return wire
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const id = item.id || item.value || item.option_id || '';
      const label = item.label || item.name || item.title || id;
      if (!id && !label) return null;
      return { id: String(id), label: String(label), is_correct: !!item.is_correct };
    })
    .filter(Boolean);
}

export function optionsSummary(card) {
  const options = normalizeCardOptions(card);
  if (!options.length) {
    const cat = card?.category || card?.card_type || card?.bucket;
    if (cat === 'news' || cat === 'fan_story') return 'Read More (no options)';
    return '—';
  }
  if (options.length <= 3) return options.map((o) => o.label).join(' · ');
  return `${options.length} options`;
}

export function sourceTypeLabel(sourceType) {
  if (!sourceType) return '';
  if (sourceType === 'instinct_fanwars') return 'FW-01 / Fan Wars';
  if (sourceType === 'instinct_winner' || String(sourceType).startsWith('instinct_pred')) {
    return 'PRED / Instinct';
  }
  if (String(sourceType).startsWith('knowledge_stat')) return 'TRIVIA / Knowledge';
  if (sourceType === 'daily_knowledge' || sourceType === 'knowledge_daily') return 'Daily knowledge';
  if (sourceType === 'hot_take_admin') return 'Admin hot take';
  return sourceType;
}

/** Standalone opinion card written on the Hot Takes tab (not a story/news child). */
export function isAdminHotTake(card) {
  return card?.source_type === 'hot_take_admin';
}

export function emptyAdminHotTakeForm() {
  return {
    title: '',
    summary: '',
    image_url: '',
    hero_presentation: '',
    hero_aspect_ratio: '',
    reward_fe: 5,
    entry_fee_fe: 0,
    status: 'active',
    starts_at: '',
    deadline_at: '',
    expires_at: '',
    options: [
      { id: 'yes', label: 'Agree' },
      { id: 'no', label: 'Disagree' },
    ],
  };
}

/** Admin hot takes: exactly 2 or 4 options, joystick labels 2–9 chars. */
export function adminHotTakeOptionError(options) {
  const rows = normalizeOptionRows(options);
  if (rows.length !== 2 && rows.length !== 4) {
    return 'Admin hot takes need exactly 2 or 4 options.';
  }
  const bad = rows.find(
    (row) =>
      row.label.length < COPY_LIMITS.optionLabel.min ||
      row.label.length > COPY_LIMITS.optionLabel.max
  );
  if (bad) {
    return `Option labels must be ${COPY_LIMITS.optionLabel.soft} characters — the joystick cannot fit longer labels.`;
  }
  return '';
}

/** Fresh-app cycle. Admin hot take leads only when one is live. */
export const PULSE_DECK_CYCLE =
  'admin hot take (if any are live) → trivia → fan story → linked hot take → one game → news → linked hot take';

export function isReadMoreCard(card) {
  const cat = card?.category || card?.card_type || card?.bucket;
  return cat === 'news' || cat === 'fan_story';
}

/** Hero crop is Fan Story only (portrait posts → 4:3 around a focus point). */
export function isFanStoryCard(card) {
  const cat = card?.category || card?.card_type || card?.bucket;
  return cat === 'fan_story';
}

/** 0–1 focus. Null until an admin crops → slider default 0.5. */
export function clampHeroFocus(value) {
  if (value == null || value === '') return 0.5;
  const n = Number(value);
  if (!Number.isFinite(n)) return 0.5;
  return Math.min(1, Math.max(0, n));
}

/** Original used for every recrop; falls back to the current hero before the first crop. */
export function heroSourceUrl(card) {
  if (!card) return '';
  return card.hero_source_url || card.heroSourceUrl || card.image_url || '';
}

export function isHotTakeCard(card) {
  const cat = card?.category || card?.card_type || card?.bucket;
  return cat === 'hot_take';
}

/** Match Rating is served as hot_take; layout.kind rating is the Rate Player pill. */
export function isMatchRatingCard(card) {
  if (!card) return false;
  const layout =
    card.layout ||
    card.payload?.layout ||
    card.payload?.wire?.layout ||
    card.wire?.layout ||
    {};
  return layout.kind === 'rating' || card.layout_kind === 'rating';
}

export function isMatchTiedPredictTrivia(card) {
  if (!card) return false;
  const cat = card.category || card.card_type || card.bucket;
  if (cat !== 'instinct' && cat !== 'knowledge' && cat !== 'fan_wars') return false;
  if (card.match_id != null && card.match_id !== '') return true;
  return Array.isArray(card.option_candidates) && card.option_candidates.length > 0;
}

export function isKnowledgeCard(card) {
  if (!card) return false;
  const cat = card.category || card.card_type || card.bucket;
  if (cat === 'knowledge') return true;
  return String(card.source_type || '').startsWith('knowledge_');
}

export function isInstinctCard(card) {
  if (!card) return false;
  const cat = card.category || card.card_type || card.bucket;
  if (cat === 'instinct' || cat === 'fan_wars') return true;
  return String(card.source_type || '').startsWith('instinct_');
}

/** Trivia may set correct; predictions settle after match — no Correct radio. */
export function canEditCorrectOption(card) {
  if (!card) return false;
  if (card.can_edit_correct_option === true) return true;
  if (card.can_edit_correct_option === false) return false;
  if (card.has_correct_answer === false) return false;
  return isKnowledgeCard(card);
}

export function resolveCorrectOptionId(card) {
  if (!card) return '';
  if (card.correct_option_id != null && card.correct_option_id !== '') {
    return String(card.correct_option_id);
  }
  const marked = (card.options || []).find((o) => o && o.is_correct);
  if (marked?.id != null && marked.id !== '') return String(marked.id);
  return '';
}

export function emptyHotTakeForm() {
  return {
    statement: '',
    options: [
      { id: 'opt_1', label: 'Agree' },
      { id: 'opt_2', label: 'Disagree' },
    ],
  };
}

export function hotTakeToForm(hotTake) {
  if (!hotTake) return emptyHotTakeForm();
  const options = Array.isArray(hotTake.options) && hotTake.options.length
    ? hotTake.options.map((o) => ({ id: o.id || '', label: o.label || '' }))
    : emptyHotTakeForm().options;
  return {
    statement: hotTake.statement || '',
    options,
  };
}

/** Statement-only grammar fixes are enough; options optional unless changed. */
export function diffHotTakePayload(originalHotTake, form, { optionsLocked = false } = {}) {
  if (!form) return null;
  const prev = hotTakeToForm(originalHotTake);
  const statement = String(form.statement || '').trim();
  const options = normalizeOptionRows(form.options);
  const prevOptions = normalizeOptionRows(prev.options);

  const statementChanged = statement !== String(prev.statement || '').trim();
  const optionsChanged = !sameOptions(options, prevOptions);

  if (!statementChanged && !optionsChanged) return null;
  if (statement.length > COPY_LIMITS.question.max) {
    return { __error: `Hot take question should be ≤${COPY_LIMITS.question.max} characters.` };
  }

  if (statementChanged && !optionsChanged) {
    return { statement };
  }

  if (options.length < 2) {
    return { __error: 'Hot take needs at least 2 options.' };
  }

  if (optionsLocked && !sameOptionIds(options, prevOptions)) {
    return {
      __error:
        'Hot take options are locked — you can change labels (same ids) but not add, remove, or change option ids.',
    };
  }

  const payload = { options };
  if (statementChanged || !originalHotTake) payload.statement = statement;
  return payload;
}

export function formatRate(value) {
  if (value == null || Number.isNaN(Number(value))) return '0.0%';
  return `${Number(value).toFixed(1)}%`;
}

export function slugFromLabel(label, fallback = 'opt') {
  const slug = String(label || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40);
  return slug || fallback;
}

export function ensureOptionIds(options) {
  const seen = new Set();
  return (options || []).map((option, index) => {
    let id = String(option.id || slugFromLabel(option.label, `opt-${index + 1}`)).trim();
    const base = id;
    let n = 2;
    while (seen.has(id)) {
      id = `${base}-${n}`;
      n += 1;
    }
    seen.add(id);
    return { id, label: String(option.label || '').trim() };
  });
}

export function emptyKnowledgeForm() {
  return {
    title: '',
    summary: '',
    image_url: '',
    hero_presentation: '',
    hero_aspect_ratio: '',
    reward_fe: 0,
    entry_fee_fe: 0,
    status: 'active',
    match_id: '',
    starts_at: '',
    deadline_at: '',
    expires_at: '',
    correct_option_id: '',
    options: [emptyOption(), emptyOption()],
  };
}

export function emptyAnalyticsTotals() {
  return {
    cards: 0,
    submissions: 0,
    unique_submissions: 0,
    unique_views: 0,
    awarded: 0,
    views: 0,
    skips: 0,
    fe_awarded: 0,
    fe_pending: 0,
    fe_spent: 0,
  };
}

/** Use response totals as-is — do not re-sum category×bucket items. */
export function parseAnalyticsPayload(data) {
  if (!data) {
    return {
      items: [],
      totals: emptyAnalyticsTotals(),
      date_from: null,
      date_to: null,
      category: null,
      categories: null,
      metric_notes: null,
    };
  }
  const items = data.items || data.categories || [];
  return {
    items: Array.isArray(items) ? items : [],
    totals: { ...emptyAnalyticsTotals(), ...(data.totals || {}) },
    date_from: data.date_from ?? null,
    date_to: data.date_to ?? null,
    category: data.category ?? null,
    categories: data.categories ?? null,
    metric_notes: data.metric_notes || null,
  };
}

export function cardToForm(card) {
  if (!card) {
    return {
      title: '',
      summary: '',
      image_url: '',
      hero_presentation: '',
      hero_aspect_ratio: '',
      reward_fe: 0,
      entry_fee_fe: 0,
      bucket: 'instinct',
      priority: 0,
      starts_at: '',
      deadline_at: '',
      expires_at: '',
      status: 'draft',
      options: [emptyOption()],
    };
  }
  const normalized = normalizeCardOptions(card);
  const options = normalized.length ? normalized : [emptyOption()];
  const wireHero = card.payload?.wire?.hero_image || card.wire?.hero_image || {};
  return {
    title: card.title || '',
    summary: card.summary || '',
    image_url: card.image_url || '',
    hero_presentation: normalizeHeroPresentation(
      card.hero_presentation ?? wireHero.presentation
    ),
    hero_aspect_ratio: normalizeHeroAspectRatio(
      card.hero_aspect_ratio ?? wireHero.aspect_ratio
    ),
    reward_fe: card.reward_fe ?? 0,
    entry_fee_fe: card.entry_fee_fe ?? 0,
    bucket: card.bucket || card.category || 'instinct',
    priority: card.priority ?? 0,
    starts_at: isoToDatetimeLocal(card.starts_at),
    deadline_at: isoToDatetimeLocal(card.deadline_at),
    expires_at: isoToDatetimeLocal(card.expires_at),
    status: card.status || 'draft',
    options,
  };
}

export function diffCardPayload(original, form) {
  const payload = {};
  const originalForm = cardToForm(original);

  const stringFields = ['title', 'summary', 'image_url', 'bucket', 'status', 'hero_aspect_ratio'];
  stringFields.forEach((field) => {
    if (String(form[field] ?? '') !== String(originalForm[field] ?? '')) {
      if (field === 'summary' && form[field] === '') {
        payload[field] = null;
      } else if (field === 'hero_aspect_ratio' && form[field] === '') {
        payload[field] = null;
      } else {
        payload[field] = form[field];
      }
    }
  });

  // Empty / Default → null clears to FE bleed
  if (String(form.hero_presentation ?? '') !== String(originalForm.hero_presentation ?? '')) {
    payload.hero_presentation = form.hero_presentation
      ? normalizeHeroPresentation(form.hero_presentation)
      : null;
  }

  ['reward_fe', 'entry_fee_fe', 'priority'].forEach((field) => {
    if (Number(form[field]) !== Number(originalForm[field])) {
      payload[field] = Number(form[field]) || 0;
    }
  });

  ['starts_at', 'deadline_at', 'expires_at'].forEach((field) => {
    const next = datetimeLocalToIso(form[field]);
    const prev = datetimeLocalToIso(originalForm[field]);
    if (next !== prev) payload[field] = next;
  });

  if (!sameOptions(form.options, originalForm.options)) {
    const nextOpts = normalizeOptionRows(form.options);
    const prevOpts = normalizeOptionRows(originalForm.options);

    if (original?.options_locked) {
      // Locked: labels only (same ids). Id add/remove/change → 409 — omit invalid diffs.
      if (sameOptionIds(nextOpts, prevOpts)) {
        payload.options = nextOpts;
      } else {
        payload.__options_lock_error =
          'Options are locked — you can change labels (same ids) but not add, remove, or change option ids.';
      }
    } else {
      payload.options = nextOpts;
    }
  }

  return payload;
}

export function parseMatchesPayload(data) {
  if (!data) return [];
  const list = data.items || data.matches || data;
  return Array.isArray(list) ? list : [];
}

export function parseCompetitionsPayload(data) {
  if (!data) return [];
  const list = data.items || data.competitions || data;
  return Array.isArray(list) ? list : [];
}

/** Missing competition settings row = on (opt-out). Explicit false = blocked. */
export function competitionFlagOn(value) {
  return value !== false;
}

/**
 * Fan Wars / Ratings default on when the field is missing (migration 216).
 * Explicit false = off.
 */
export function matchFlagOn(value) {
  return value !== false;
}

export function matchBlockedByCompetition(match) {
  if (!match) return null;
  if (match.competition_pulse_enabled === false) {
    return 'Competition Pulse off — generators skip this fixture';
  }
  const off = [];
  if (match.competition_instinct_enabled === false) off.push('Instinct');
  if (match.competition_knowledge_enabled === false) off.push('Knowledge');
  if (match.competition_fanwars_enabled === false) off.push('Fan Wars');
  if (match.competition_ratings_enabled === false) off.push('Ratings');
  if (!off.length) return null;
  return `Competition ${off.join(' & ')} off`;
}

export function competitionLabel(match) {
  if (!match) return '';
  return (
    match.competition_abbr ||
    match.competition_title ||
    (match.competition_cid != null ? `CID ${match.competition_cid}` : '')
  );
}

export function parseCardsPayload(data) {
  if (!data) return { items: [], total: 0, limit: 30, offset: 0 };
  const items = data.items || data.cards || [];
  return {
    items: Array.isArray(items) ? items : [],
    total: data.total ?? (Array.isArray(items) ? items.length : 0),
    limit: data.limit ?? 30,
    offset: data.offset ?? 0,
  };
}

export function parseCardsSummaryPayload(data) {
  if (!data) {
    return {
      total_active: 0,
      by_category: {
        instinct: 0,
        knowledge: 0,
        news: 0,
        fan_story: 0,
        hot_take: 0,
        fan_wars: 0,
      },
    };
  }
  return {
    total_active: data.total_active ?? data.totalActive ?? 0,
    by_category: {
      instinct: 0,
      knowledge: 0,
      news: 0,
      fan_story: 0,
      hot_take: 0,
      fan_wars: 0,
      ...(data.by_category || data.byCategory || {}),
    },
  };
}

export function parseCardPayload(data) {
  if (!data) return null;
  if (data.card) {
    return {
      ...data.card,
      stats: data.stats || data.card.stats,
      hot_take: data.hot_take !== undefined ? data.hot_take : data.card.hot_take,
      option_candidates:
        data.option_candidates || data.card.option_candidates || [],
    };
  }
  return data;
}

const DEFAULT_CLOUDFLARE_HINT =
  'Search logs for pulse:<userId> or pulse:install:<installationId>';

/** GET /admin/pulse/actors list envelope */
export function parseActorsPayload(data) {
  if (!data) {
    return {
      items: [],
      total: 0,
      limit: 40,
      offset: 0,
      source: null,
      cloudflare_hint: DEFAULT_CLOUDFLARE_HINT,
    };
  }
  const items = data.items || data.actors || [];
  return {
    items: Array.isArray(items) ? items : [],
    total: data.total ?? (Array.isArray(items) ? items.length : 0),
    limit: data.limit ?? 40,
    offset: data.offset ?? 0,
    source: data.source ?? null,
    cloudflare_hint: data.cloudflare_hint || data.cloudflareHint || DEFAULT_CLOUDFLARE_HINT,
  };
}

/**
 * Resolve Cloudflare / CloudWatch search key for an actor row.
 * Prefer API pulse_log_key; fall back to pulse:<userId> / pulse:install:<id>.
 */
export function actorPulseLogKey(actor) {
  if (!actor) return '';
  if (actor.pulse_log_key) return String(actor.pulse_log_key);
  if (actor.user_id != null && actor.user_id !== '') return `pulse:${actor.user_id}`;
  if (actor.installation_id) return `pulse:install:${actor.installation_id}`;
  if (actor.subject_type === 'user' && actor.subject_id) return `pulse:${actor.subject_id}`;
  if (actor.subject_type === 'installation' && actor.subject_id) {
    return `pulse:install:${actor.subject_id}`;
  }
  return '';
}

/** Build /admin/pulse/actors/order query from an actor row or free-form search. */
export function actorOrderQueryFromActor(actor) {
  if (!actor) return {};
  const key = actorPulseLogKey(actor);
  if (key) return { key };
  if (actor.user_id != null && actor.user_id !== '') return { user_id: actor.user_id };
  if (actor.installation_id) return { installation_id: actor.installation_id };
  if (actor.guest_hash) return { guest_hash: actor.guest_hash };
  return {};
}

/** Parse free-form support search into actors/order query params. */
export function parseActorOrderSearch(raw) {
  const q = String(raw || '').trim();
  if (!q) return {};
  if (/^pulse:/i.test(q)) return { key: q };
  if (/^\d+$/.test(q)) return { user_id: q, key: `pulse:${q}` };
  if (/^install(ation)?[:/]/i.test(q)) {
    const id = q.replace(/^install(ation)?[:/]/i, '').trim();
    return id ? { installation_id: id, key: `pulse:install:${id}` } : {};
  }
  if (q.length >= 8 && !/\s/.test(q)) {
    // Likely installation id or guest hash
    return { installation_id: q, key: `pulse:install:${q}` };
  }
  return { key: q };
}

/** GET /admin/pulse/actors/order detail envelope */
export function parseActorOrderPayload(data) {
  if (!data) {
    return {
      current_order: { items: [], total: 0 },
      why: null,
      continue_pin: null,
      seen_count: 0,
      done_count: 0,
      recent_hits: [],
      actor: null,
      limit: 50,
      offset: 0,
    };
  }
  const orderBlock = data.current_order || data.currentOrder || {};
  const orderItems = orderBlock.items || orderBlock.cards || data.items || [];
  return {
    current_order: {
      items: Array.isArray(orderItems) ? orderItems : [],
      total:
        orderBlock.total ??
        data.total ??
        (Array.isArray(orderItems) ? orderItems.length : 0),
    },
    why: data.why ?? null,
    continue_pin: data.continue_pin || data.continuePin || null,
    seen_count: data.seen_count ?? data.seenCount ?? 0,
    done_count: data.done_count ?? data.doneCount ?? 0,
    recent_hits: Array.isArray(data.recent_hits)
      ? data.recent_hits
      : Array.isArray(data.recentHits)
        ? data.recentHits
        : [],
    actor: data.actor || null,
    pulse_log_key: data.pulse_log_key || data.pulseLogKey || null,
    limit: data.limit ?? 50,
    offset: data.offset ?? 0,
    ...data,
  };
}

export function formatWhyCard(why) {
  if (why == null || why === '') return '—';
  if (typeof why === 'string') return why;
  if (typeof why === 'object') {
    if (why.reason) return String(why.reason);
    if (why.label) return String(why.label);
    try {
      return JSON.stringify(why);
    } catch {
      return '—';
    }
  }
  return String(why);
}
